'use client';

import { ProductImage, ProductProps } from '@/types/Product';
import {
  Dialog,
  Flex,
  Grid,
  Icon,
  IconButton,
  Portal,
  Tabs,
  Text,
} from '@chakra-ui/react';
import Image from 'next/image';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MdOutlineArrowBackIos } from 'react-icons/md';
import { useRouter } from 'next/navigation';
import { FaChevronLeft, FaChevronRight, FaXmark } from 'react-icons/fa6';
import { LuListMinus, LuStar, LuTable } from 'react-icons/lu';
import ProductOptions from './Options';
import ProductReviews from './Reviews';
import ArrowButton from '../ArrowButton';
import { CartContext } from '@/contexts/CartContext';
import { pushDataLayerEvent, toEcommerceItem } from '@/utils/dataLayer';
import { trackMetaEvent } from '@/utils/metaPixel';
import { useWhatsAppTopic } from '@/contexts/WhatsAppContext';

const ProductPage = ({ product }: { product: ProductProps }) => {
  const [mainImage, setMainImage] = useState<ProductImage | null>(null);
  const [images, setImages] = useState<ProductImage[]>([]);
  const [viewImageFull, setViewImageFull] = useState<boolean>(false);

  const { handleSetViewProductCookie } = useContext(CartContext);

  useWhatsAppTopic(
    product
      ? {
          label: `Dúvida sobre ${product.name}`,
          message: `Olá! Tenho uma dúvida sobre o produto "${product.name}".`,
          includeUrl: true,
        }
      : null,
  );

  const gridRef = useRef<HTMLDivElement | null>(null);

  const { back } = useRouter();

  const handleOptionChange = useCallback(
    (optionId: string) => {
      if (product && product.type_opt === 2) {
        const selectedImage = product.images.filter(
          (image) =>
            !image.hide_after_selection &&
            (image.variation_id === optionId ||
              (image.variation_id === null && image.color_id === null)),
        );
        setMainImage(selectedImage.length > 0 ? selectedImage[0] : null);
        setImages(selectedImage.length > 0 ? selectedImage : product.images);
      }
      if (product && product.type_opt === 3) {
        const selectedImage = product.images.filter(
          (image) =>
            !image.hide_after_selection &&
            (image.color_id === optionId ||
              (image.variation_id === null && image.color_id === null)),
        );
        setMainImage(selectedImage.length > 0 ? selectedImage[0] : null);
        setImages(selectedImage.length > 0 ? selectedImage : product.images);
      }
    },
    [product],
  );

  const handleImageClick = (image: ProductImage) => {
    setMainImage(image);
  };

  const currentImageIndex = images.findIndex(
    (image) => image.id === mainImage?.id,
  );

  const showPrevImage = useCallback(() => {
    setMainImage((prev) => {
      if (!prev || images.length === 0) return prev;
      const index = images.findIndex((image) => image.id === prev.id);
      return images[(index - 1 + images.length) % images.length];
    });
  }, [images]);

  const showNextImage = useCallback(() => {
    setMainImage((prev) => {
      if (!prev || images.length === 0) return prev;
      const index = images.findIndex((image) => image.id === prev.id);
      return images[(index + 1) % images.length];
    });
  }, [images]);

  useEffect(() => {
    if (!viewImageFull) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') showPrevImage();
      if (e.key === 'ArrowRight') showNextImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewImageFull, showPrevImage, showNextImage]);

  const scrollLeft = () => {
    if (gridRef.current) {
      gridRef.current.scrollBy({ left: -130, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (gridRef.current) {
      gridRef.current.scrollBy({ left: 130, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (product) {
      handleSetViewProductCookie(product.slug);

      // Produto "brevemente" não tem preço público — sem `value` a reportar,
      // e um view_item a 0€ só poluiria as métricas/audiências.
      if (product.coming_soon) return;

      const item = toEcommerceItem(product);
      pushDataLayerEvent('view_item', {
        ecommerce: { currency: 'EUR', value: item.price, items: [item] },
      });
      trackMetaEvent('ViewContent', {
        content_ids: [item.item_id],
        content_type: 'product',
        content_name: item.item_name,
        currency: 'EUR',
        value: item.price,
      });
    }
  }, [product, handleSetViewProductCookie]);

  useEffect(() => {
    if (product) {
      const defaultImage =
        product.images.find((img) => img.default) || product.images[0] || null;
      setMainImage(defaultImage);
      setImages(product.images);
    }
  }, [product]);

  if (!product) return <Flex />;

  return (
    <Flex gap={{ base: '20px', md: '28px' }} flexDir="column">
      {/* Back button */}
      <Flex>
        <Flex
          align="center"
          gap="8px"
          _hover={{ cursor: 'pointer', bg: 'rgba(0,176,202,0.08)', borderColor: '#00B0CA' }}
          onClick={back}
          borderRadius="full"
          py="8px"
          px="18px"
          bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
          border="1px solid"
          borderColor={{ base: 'rgba(0,0,0,0.1)', _dark: 'rgba(255,255,255,0.1)' }}
          boxShadow="0 1px 4px rgba(0,0,0,0.06)"
          transition="all 0.2s"
        >
          <Icon
            fontSize="11px"
            color={{ base: 'rgba(0,0,0,0.4)', _dark: 'rgba(255,255,255,0.4)' }}
          >
            <MdOutlineArrowBackIos />
          </Icon>
          <Text fontSize="14px" fontWeight="500" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
            Voltar
          </Text>
        </Flex>
      </Flex>

      {/* Grid de 2 colunas x 2 linhas: galeria (col 1, linha 1), opções
          (col 2, a abranger as 2 linhas, para o painel sticky ter altura
          para "viajar") e separadores (col 1, linha 2). No mobile colapsa
          para 1 coluna — a ordem no DOM é o que dita o empilhamento, por
          isso as opções (que no mobile passam de fixas a inline, consoante
          o tipo de produto) ficam fisicamente entre a galeria e os
          separadores. */}
      <Grid
        templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
        columnGap={{ base: '20px', lg: '32px' }}
        rowGap="20px"
        alignItems="start"
      >
        {/* minW="0" é o que permite ao scroll lateral das miniaturas
            funcionar — sem isto, uma coluna "1fr" cresce para caber o
            conteúdo (todas as miniaturas lado a lado) em vez de o cortar
            e deixar o scroll acontecer, espremendo a coluna da direita. */}
        <Flex
          flexDir="column"
          gap="12px"
          minW="0"
          gridColumn={{ lg: '1' }}
          gridRow={{ lg: '1' }}
        >
          {/* Main image */}
          <Flex
              align="center"
              justify="center"
              _hover={{ cursor: 'pointer', boxShadow: '0 8px 48px rgba(0,176,202,0.15)' }}
              onClick={() => setViewImageFull(true)}
              bg={{ base: '#F8FAFB', _dark: '#0C1B2A' }}
              borderRadius="24px"
              border="1px solid"
              borderColor={{ base: 'rgba(0,176,202,0.12)', _dark: 'rgba(0,176,202,0.1)' }}
              boxShadow="0 4px 28px rgba(0,176,202,0.07)"
              w="100%"
              h={{ base: '280px', md: '460px' }}
              overflow="hidden"
              transition="box-shadow 0.25s"
              p="24px"
            >
              <Image
                src={mainImage?.path || '/ido-logo.png'}
                alt={mainImage?.alt || 'Imagem do produto'}
                width={600}
                height={420}
                quality={100}
                style={{ objectFit: 'contain', maxHeight: '100%', maxWidth: '100%' }}
              />
            </Flex>

            {/* Thumbnail strip */}
            {images.length > 0 && (
              <Flex pos="relative" align="center" minW="0">
                <ArrowButton onClick={scrollLeft} direction="left" />
                <Flex
                  ref={gridRef}
                  gap="8px"
                  flex="1"
                  minW="0"
                  overflowX="auto"
                  scrollbarWidth="none"
                  css={{ '&::-webkit-scrollbar': { display: 'none' } }}
                  py="4px"
                  px="44px"
                >
                  {images.map((image) => (
                    <Flex
                      key={image.id}
                      flexShrink={0}
                      w="80px"
                      h="80px"
                      align="center"
                      justify="center"
                      bg={{ base: '#F8FAFB', _dark: '#0C1B2A' }}
                      cursor="pointer"
                      onClick={() => handleImageClick(image)}
                      borderRadius="14px"
                      border="1px solid"
                      borderColor={
                        mainImage?.id === image.id
                          ? '#00B0CA'
                          : { base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.07)' }
                      }
                      boxShadow={
                        mainImage?.id === image.id
                          ? '0 0 0 3px rgba(0,176,202,0.18)'
                          : 'none'
                      }
                      transition="all 0.18s"
                      _hover={{ borderColor: '#00B0CA' }}
                      p="6px"
                      overflow="hidden"
                    >
                      <Image
                        src={image.path || '/logo-idu.svg'}
                        alt={image.alt}
                        width={80}
                        height={80}
                        quality={90}
                        style={{
                          objectFit: 'contain',
                          maxHeight: '100%',
                          maxWidth: '100%',
                        }}
                      />
                    </Flex>
                  ))}
                </Flex>
                <ArrowButton onClick={scrollRight} direction="right" />
              </Flex>
            )}

          {/* Modal para ver a imagem em tamanho grande */}
          <Dialog.Root
            open={viewImageFull}
            onOpenChange={(e) => setViewImageFull(e.open)}
            size="full"
            placement="center"
          >
            <Portal>
              <Dialog.Backdrop bg="rgba(5,12,20,0.92)" />
              <Dialog.Positioner>
                <Dialog.Content
                  bg="transparent"
                  boxShadow="none"
                  w="full"
                  h="full"
                  maxW="100vw"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  pos="relative"
                >
                  <Dialog.CloseTrigger asChild>
                    <IconButton
                      type="button"
                      aria-label="Fechar"
                      pos="absolute"
                      top={{ base: '16px', md: '24px' }}
                      right={{ base: '16px', md: '24px' }}
                      zIndex="2"
                      w="42px"
                      h="42px"
                      minW="42px"
                      borderRadius="full"
                      bg="rgba(255,255,255,0.1)"
                      color="white"
                      _hover={{ bg: 'rgba(255,255,255,0.2)' }}
                    >
                      <FaXmark size="16px" />
                    </IconButton>
                  </Dialog.CloseTrigger>

                  {images.length > 1 && (
                    <IconButton
                      type="button"
                      aria-label="Imagem anterior"
                      onClick={showPrevImage}
                      pos="absolute"
                      left={{ base: '8px', md: '24px' }}
                      top="50%"
                      transform="translateY(-50%)"
                      zIndex="2"
                      w="46px"
                      h="46px"
                      minW="46px"
                      borderRadius="full"
                      bg="rgba(255,255,255,0.1)"
                      color="white"
                      _hover={{ bg: 'rgba(255,255,255,0.2)' }}
                    >
                      <FaChevronLeft size="16px" />
                    </IconButton>
                  )}

                  {mainImage?.path && (
                    <Image
                      src={mainImage.path}
                      alt={mainImage.alt || 'Imagem do produto'}
                      width={1200}
                      height={1200}
                      quality={100}
                      style={{
                        objectFit: 'contain',
                        maxWidth: '80vw',
                        maxHeight: '84vh',
                        width: 'auto',
                        height: 'auto',
                      }}
                    />
                  )}

                  {images.length > 1 && (
                    <IconButton
                      type="button"
                      aria-label="Imagem seguinte"
                      onClick={showNextImage}
                      pos="absolute"
                      right={{ base: '8px', md: '24px' }}
                      top="50%"
                      transform="translateY(-50%)"
                      zIndex="2"
                      w="46px"
                      h="46px"
                      minW="46px"
                      borderRadius="full"
                      bg="rgba(255,255,255,0.1)"
                      color="white"
                      _hover={{ bg: 'rgba(255,255,255,0.2)' }}
                    >
                      <FaChevronRight size="16px" />
                    </IconButton>
                  )}

                  {images.length > 1 && (
                    <Flex
                      pos="absolute"
                      bottom={{ base: '16px', md: '24px' }}
                      left="50%"
                      transform="translateX(-50%)"
                      gap="6px"
                      align="center"
                    >
                      <Text fontSize="12px" fontWeight="600" color="rgba(255,255,255,0.75)">
                        {currentImageIndex + 1} / {images.length}
                      </Text>
                    </Flex>
                  )}
                </Dialog.Content>
              </Dialog.Positioner>
            </Portal>
          </Dialog.Root>
        </Flex>

        <ProductOptions product={product} handleOptionChange={handleOptionChange} />

        {/* Separadores: Descrição | Detalhes | Avaliações */}
        <Flex
          gridColumn={{ lg: '1' }}
          gridRow={{ lg: '2' }}
          bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
          borderRadius="20px"
          border="1px solid"
          borderColor={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.06)' }}
          boxShadow="0 2px 12px rgba(0,0,0,0.04)"
          overflow="hidden"
        >
          <Tabs.Root defaultValue="description" variant="line" w="100%">
              <Tabs.List
                bg={{ base: '#F5F7F9', _dark: '#0D2035' }}
                px="8px"
                pt="6px"
              >
                <Tabs.Trigger
                  value="description"
                  flex="1"
                  alignItems="center"
                  justifyContent="center"
                  fontSize={{ base: '13px', md: '15px' }}
                  fontWeight="600"
                  pb="12px"
                >
                  <Icon mr="6px" fontSize="15px"><LuListMinus /></Icon>
                  Descrição
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="features"
                  flex="1"
                  alignItems="center"
                  justifyContent="center"
                  fontSize={{ base: '13px', md: '15px' }}
                  fontWeight="600"
                  pb="12px"
                >
                  <Icon mr="6px" fontSize="15px"><LuTable /></Icon>
                  Detalhes
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="avaluations"
                  flex="1"
                  alignItems="center"
                  justifyContent="center"
                  fontSize={{ base: '13px', md: '15px' }}
                  fontWeight="600"
                  pb="12px"
                >
                  <Icon mr="6px" fontSize="15px"><LuStar /></Icon>
                  Avaliações
                </Tabs.Trigger>
              </Tabs.List>

              {/* Description tab */}
              <Tabs.Content value="description">
                <Flex p={{ base: '20px', md: '32px' }}>
                  {product.description ? (
                    <Text
                      fontSize={{ base: '14px', md: '16px' }}
                      lineHeight="1.9"
                      whiteSpace="pre-line"
                      color={{ base: 'rgba(0,0,0,0.72)', _dark: 'rgba(255,255,255,0.72)' }}
                    >
                      {product.description}
                    </Text>
                  ) : (
                    <Text
                      fontSize="15px"
                      color={{ base: 'rgba(0,0,0,0.35)', _dark: 'rgba(255,255,255,0.3)' }}
                    >
                      Sem descrição disponível.
                    </Text>
                  )}
                </Flex>
              </Tabs.Content>

              {/* Features tab */}
              <Tabs.Content value="features" w="100%">
                {product.features && product.features.length > 0 ? (
                  <Flex flexDir="column">
                    {product.features.map((feature, index) => (
                      <Flex
                        key={index}
                        align="center"
                        borderBottom="1px solid"
                        borderColor={{ base: 'rgba(0,0,0,0.05)', _dark: 'rgba(255,255,255,0.05)' }}
                        _hover={{
                          bg: { base: 'rgba(0,176,202,0.03)', _dark: 'rgba(0,176,202,0.04)' },
                        }}
                        transition="background 0.15s"
                        px={{ base: '16px', md: '28px' }}
                        py="14px"
                      >
                        <Text
                          w={{ base: '40%', md: '30%' }}
                          fontSize={{ base: '13px', md: '14px' }}
                          fontWeight="600"
                          color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
                        >
                          {feature.name}
                        </Text>
                        <Text
                          w={{ base: '60%', md: '70%' }}
                          fontSize={{ base: '13px', md: '14px' }}
                          color={{ base: 'rgba(0,0,0,0.65)', _dark: 'rgba(255,255,255,0.6)' }}
                        >
                          {feature.value}
                        </Text>
                      </Flex>
                    ))}
                  </Flex>
                ) : (
                  <Flex p="24px" justify="center">
                    <Text
                      fontSize="15px"
                      color={{ base: 'rgba(0,0,0,0.35)', _dark: 'rgba(255,255,255,0.3)' }}
                    >
                      Nenhuma característica disponível.
                    </Text>
                  </Flex>
                )}
              </Tabs.Content>

              {/* Reviews tab */}
              <Tabs.Content value="avaluations">
                <ProductReviews productId={product.id} />
              </Tabs.Content>
            </Tabs.Root>
        </Flex>
      </Grid>
    </Flex>
  );
};

export default ProductPage;
